import React from 'react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { useAccreditationApplications } from '../../../hooks/useAccreditationApplications';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { LockOwner } from '../../../graphql/__generated__/graphql';
import { useAccreditations } from 'hooks/useAccreditations';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import AccreditationsBlockList from './AccreditationsBlockList';
import ApplicationsBlockList from './ApplicationsBlockList';

export type ApplicationAction = 'apply' | 'view' | 'renew';

const ApplicationsBlock: React.FC = () => {
    const notifications = useNotificationContext();
    const navigate = useNavigate();

    const {
        models: { loading: applicationsLoading, updating, accreditationTypes, applications },
        operations: { deleteApplication, fetchApplications, submitApplication },
    } = useAccreditationApplications();
    const {
        models: {
            accreditations: { active, expired, renewable },
            loading: accreditationsLoading,
        },
    } = useAccreditations();

    if (applicationsLoading || accreditationsLoading) return <LoadingSpinner />;

    const handleOnSelectApplication = (
        application: IAccreditationApplication,
        action: ApplicationAction
    ) => {
        switch (action) {
            case 'apply':
                if (application.accreditationApplicationId) {
                    navigate(`/accreditation/apply/${application.accreditationApplicationId}`);
                } else {
                    navigate(
                        `/accreditation/apply/prerequisites/${application.accreditationType.accreditationTypeId}`
                    );
                }
                return;
            case 'renew':
                if (application.accreditationApplicationId) {
                    navigate(`/accreditation/renew/${application.accreditationApplicationId}`);
                } else {
                    navigate(
                        `/accreditation/renew/prerequisites/${application.accreditationType.accreditationTypeId}`
                    );
                }
                return;
            case 'view':
                navigate(`/accreditation/view/${application.accreditationApplicationId}`);
                return;
        }
    };

    const handleOnAbandonApplication = async (application: IAccreditationApplication) => {
        try {
            await deleteApplication(application);
            notifications.success('Application abandoned successfully');
        } catch (e) {
            console.error('Something went wrong abandoning application', e);
            notifications.error('Something went wrong whilst abandoning the application.');
        } finally {
            await fetchApplications();
        }
    };

    const handleOnSubmitForReview = async (application: IAccreditationApplication) => {
        return submitApplication(application, LockOwner.Supervisor)
            .then(() => {
                navigate('/accreditation?formSubmitType=supervisor', { replace: false });
                return this;
            })
            .catch(() => {
                notifications.error(
                    'Something went wrong when submitting the application. Please try again later'
                );
            })
            .finally(() => {
                fetchApplications();
            });
    };

    const handleOnSubmitFinalApplication = async (application: IAccreditationApplication) => {
        submitApplication(application, LockOwner.Emdr)
            .then(() => {
                notifications.success('Application successfully submitted to the EMDR committee.');
                navigate('/accreditation?formSubmitType=emdr', { replace: false });
            })
            .catch((e) => {
                console.error('Something went wrong submitting application', e);
                notifications.error('Something went wrong when submitting your application.');
            })
            .finally(() => {
                fetchApplications();
            });
    };

    const handleOnSelectReview = (applicationReviewId: string) =>
        navigate(`/accreditation/review/${applicationReviewId}`);

    const renewalApplications: IAccreditationApplication[] = [];
    const mergedApplications: IAccreditationApplication[] = [];

    accreditationTypes.forEach((accreditationType) => {
        if (renewable.includes(accreditationType.accreditationTypeId)) {
            // Conditional check to satisfy TypeScript thinking it might not exist
            const application = {
                ...(applications.find((app) => {
                    return (
                        app.isRenewal &&
                        app.accreditationType.accreditationTypeId ===
                            accreditationType.accreditationTypeId
                    );
                }) ?? {
                    jsonData: '{}',
                    hasMitigation: false,
                    isRenewal: true,
                }),
            };
            renewalApplications.push({
                ...application,
                accreditationType,
            });
        } else {
            const application = {
                ...(applications.find(
                    (app) =>
                        app.accreditationType.accreditationTypeId ===
                        accreditationType.accreditationTypeId
                ) ?? {
                    jsonData: '{}',
                    hasMitigation: false,
                    isRenewal: false,
                }),
            };
            mergedApplications.push({ ...application, accreditationType: accreditationType });
        }
    });

    return (
        <>
            {expired?.length > 0 && (
                <AccreditationsBlockList
                    accreditations={expired}
                    applications={renewalApplications}
                    header={'Expired Accreditations'}
                    onSelectApplication={handleOnSelectApplication}
                    onSubmitFinalApplication={handleOnSubmitFinalApplication}
                    onSubmitForReview={handleOnSubmitForReview}
                />
            )}
            {active?.length > 0 && (
                <AccreditationsBlockList
                    accreditations={active}
                    applications={renewalApplications}
                    header={'Active Accreditations'}
                    onSelectApplication={handleOnSelectApplication}
                    onSubmitFinalApplication={handleOnSubmitFinalApplication}
                    onSubmitForReview={handleOnSubmitForReview}
                />
            )}

            <ApplicationsBlockList
                header={active.length > 0 ? 'Add extra accreditations' : 'Apply for accreditation'}
                applications={mergedApplications}
                onSelectApplication={handleOnSelectApplication}
                onSelectReview={handleOnSelectReview}
                onAbandonApplication={handleOnAbandonApplication}
                onSubmitFinalApplication={handleOnSubmitFinalApplication}
                onSubmitForReview={handleOnSubmitForReview}
                updating={updating}
            />
        </>
    );
};

export default ApplicationsBlock;
